import React from 'react';
import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';

import PromoCodeInput from './PromoCodeInput';
import { useCheckoutContext } from '../../../state/CheckoutContext';

import styles from './PricingList.scss';

export const I18nPrefix = 'strava.subscriptions.checkout.summary.organic';

const renderPerMonthPrice = (price, currency) =>
  I18n.t_html(
    `${I18nPrefix}.per_month`,
    {
      price: Cldr.formatCurrency(price, currency)
    },
    {
      tagName: 'div'
    }
  );

const renderPerYearPrice = (price, currency) =>
  I18n.t_html(
    `${I18nPrefix}.per_year`,
    {
      price: Cldr.formatCurrency(price, currency)
    },
    {
      tagName: 'div'
    }
  );

export const PricingList = () => {
  const { checkoutEntityState } = useCheckoutContext();

  const {
    duration,
    isTrial,
    products,
    promoCodeInputVisibility: isPromoCodeInputVisible
  } = checkoutEntityState;

  const cartData = products?.[duration];
  const promotionApplied = !!products?.promotion?.code;

  const discountItemText = (discount) =>
    `${I18n.t(discount.nameKey)} (-${Math.round(discount.percent)}%)`;

  const isYearlyDuration = () => {
    return cartData.duration === 'yearly';
  };

  const totalPrimaryPrice = () => {
    if (isTrial) {
      return isYearlyDuration() ? cartData.yearlyTotal : cartData.monthlyTotal;
    }

    return cartData.billedToday;
  };

  const totalSecondaryPrice = () => {
    if (isTrial) {
      return isYearlyDuration() ? cartData.monthlyTotal : cartData.yearlyTotal;
    }

    return cartData.billedTodayOther;
  };

  const { currency } = cartData;
  const shouldShowPromoInput = !promotionApplied && isPromoCodeInputVisible;

  const subtotalText = isYearlyDuration()
    ? renderPerYearPrice(cartData.yearlyTotal, currency)
    : renderPerMonthPrice(cartData.monthlyTotal, currency);

  const totalPrimaryText = isYearlyDuration()
    ? renderPerYearPrice(totalPrimaryPrice(), currency)
    : renderPerMonthPrice(totalPrimaryPrice(), currency);

  const totalSecondaryText = isYearlyDuration()
    ? renderPerMonthPrice(totalSecondaryPrice(), currency)
    : renderPerYearPrice(totalSecondaryPrice(), currency);

  return (
    <div className={styles.pricingList}>
      {cartData.discounts.length > 0 && (
        <>
          <div className={styles.summaryRow} data-testid="summary-row">
            <div>{I18n.t(`${I18nPrefix}.subtotal`)}</div>
            <div className={styles.priceText}>{subtotalText}</div>
          </div>
          {cartData.discounts.map((discount) => (
            <div
              className={styles.summaryRow}
              key={discount.nameKey}
              data-testid="summary-row"
            >
              <div>{discountItemText(discount)}</div>
              <div className={styles.priceText}>
                {isYearlyDuration()
                  ? renderPerYearPrice(-discount.amount, currency)
                  : renderPerMonthPrice(-discount.amount, currency)}
              </div>
            </div>
          ))}
          <div className={styles.divider} />
        </>
      )}

      {shouldShowPromoInput && (
        <>
          <PromoCodeInput />
          <div className={styles.divider} />
        </>
      )}

      <div className={styles.totals}>
        <div data-testid="price-list-total-text">
          {isTrial
            ? I18n.t(`${I18nPrefix}.total_trial`)
            : I18n.t(`${I18nPrefix}.total`)}
        </div>
        <div className="text-right">
          <div
            className={styles.totalPrimaryText}
            data-testid="price-list-total-primary-text"
          >
            {totalPrimaryText}
          </div>
          <div
            className={styles.totalSecondaryText}
            data-testid="price-list-total-secondary-text"
          >
            {totalSecondaryText}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingList;
